/**
 * Currency System Constants
 * 
 * Currency codes, symbols, exchange rates, and display configurations
 */

import { CurrencyCode, ExchangeRates } from '../types/currency.types';

/**
 * Fixed exchange rates for MVP
 * 
 * NOTE: These are fixed rates locked at implementation time.
 * For production, consider implementing a rate update mechanism
 * or using an external API for real-time rates.
 * 
 * Last updated: 2026-01-16
 * Source: Market rates (exchange-rates.org, poundsterlinglive.com)
 */
export const EXCHANGE_RATES: ExchangeRates = {
  USD: 1.00,
  AUD: 0.67, // 1 AUD = 0.67 USD (Jan 2026 avg)
  GBP: 1.34, // 1 GBP = 1.34 USD (Jan 2026 avg)
  NZD: 0.58, // 1 NZD = 0.58 USD (Jan 2026 avg)
};

/**
 * Currency symbols for display
 */
export const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  AUD: '$',
  USD: '$',
  GBP: '£',
  NZD: '$',
};

/**
 * Currency full names
 */
export const CURRENCY_NAMES: Record<CurrencyCode, string> = {
  AUD: 'Australian Dollar',
  USD: 'US Dollar',
  GBP: 'British Pound',
  NZD: 'New Zealand Dollar',
};

/**
 * Currency codes list for dropdowns/selectors
 */
export const CURRENCY_OPTIONS: Array<{ value: CurrencyCode; label: string; symbol: string }> = [
  { value: 'AUD', label: 'AUD - Australian Dollar', symbol: '$' },
  { value: 'USD', label: 'USD - US Dollar', symbol: '$' },
  { value: 'GBP', label: 'GBP - British Pound', symbol: '£' },
  { value: 'NZD', label: 'NZD - New Zealand Dollar', symbol: '$' },
];

/**
 * Default currency for new users
 * 
 * Based on project rule: "AUD IS GOOD" - default to AUD
 */
export const DEFAULT_CURRENCY: CurrencyCode = 'AUD';

/**
 * Currency detection patterns for parsing
 * 
 * Regex patterns to detect currency codes in strings
 */
export const CURRENCY_PATTERNS: Record<CurrencyCode, RegExp[]> = {
  AUD: [
    /\bAUD\b/i,
    /\bA\$/,
    /\bAU\$/,
    /Australian\s+Dollar/i,
  ],
  USD: [
    /\bUSD\b/i,
    /\bUS\$/,
    /US\s+Dollar/i,
    /American\s+Dollar/i,
  ],
  GBP: [
    /\bGBP\b/i,
    /£/,
    /\bUK\s+Pound/i,
    /British\s+Pound/i,
    /Pound\s+Sterling/i,
  ],
  NZD: [
    /\bNZD\b/i,
    /\bNZ\$/,
    /New\s+Zealand\s+Dollar/i,
  ],
};

/**
 * Format number with currency symbol and code
 * 
 * @param value - Numeric value
 * @param currency - Currency code
 * @param showCode - Whether to show currency code (e.g., "AUD")
 * @returns Formatted string
 */
export function getCurrencyDisplay(value: number, currency: CurrencyCode, showCode: boolean = false): string {
  const symbol = CURRENCY_SYMBOLS[currency];
  const formatted = value.toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  
  if (showCode) {
    return `${symbol}${formatted} ${currency}`;
  }
  
  return `${symbol}${formatted}`;
}
